import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faCalendarDays,
  faChartPie,
  faClipboardCheck,
  faEnvelopes,
  faFileLines,
  faInboxIn,
  faMagnifyingGlassDollar,
  faMessage,
  faRankingStar,
  faUserGraduate,
  faUserSecret
} from '@fortawesome/pro-solid-svg-icons'
import type { PropsWithChildren } from 'react'
import type {
  RealTimeUserNotification,
  UserNotification
} from '~/api/queries/user'
import { getApplicationModalGlobalRef } from '~/domains/applications/components/applicationModalRef'

const applicationIdsFromStack = (stack: { jobmailId?: string }[]): number[] => [
  ...new Set(
    stack
      .map((entry) => Number(entry.jobmailId))
      .filter((jobmailId) => Number.isInteger(jobmailId) && jobmailId > 0)
  )
]

/**
 * Opens the application over the current page when a modal host is mounted, and falls back to the
 * listing otherwise, which is what happens from a legacy page where the host cannot be rendered.
 */
const openApplication = (
  applicationId: number,
  applicationIds: number[],
  navigateToV3: (path: string) => void
): void => {
  if (!getApplicationModalGlobalRef().current(applicationId, applicationIds)) {
    navigateToV3(`/applications?open=${applicationId}`)
  }
}

export type NotificationHandlers = {
  openTask?: (taskId: number) => void
}

export const getNotificationMetas = (
  notification: UserNotification | RealTimeUserNotification,
  legacyUrl: string,
  navigateToV3: (path: string) => void,
  handlers: NotificationHandlers = {}
): {
  title: string
  subtitle: string
  icon: null | IconDefinition
  onClick: () => void
} => {
  let title = ''
  let subtitle = ''
  let icon = null
  let onClick = () => {}
  switch (notification.type) {
    case 'application':
      title = `${notification.metadata.details.offerName}`
      subtitle = `Il y a ${notification.metadata.count} nouvelles candidatures sur l'offre`
      if (notification.metadata.count === 1) {
        subtitle = `${notification.metadata.stack[0]?.applicantName} a postulé sur l'offre`
      }
      if (!Object.hasOwn(notification, 'isRead')) {
        subtitle = `${notification.metadata.stack[notification.metadata.stack.length - 1]?.applicantName} a postulé sur l'offre`
      }
      onClick = () => {
        const applicationIds = applicationIdsFromStack(
          notification.metadata.stack
        )
        // The flash announces the last application received, the stored notification the first one
        const applicationId = Object.hasOwn(notification, 'isRead')
          ? applicationIds[0]
          : applicationIds[applicationIds.length - 1]

        if (applicationId === undefined) {
          // Notifications stored before the stack carried the application ids
          window.location.href = `${legacyUrl}/admin/jobmail/index/0/${notification.metadata.details.offerId}`
          return
        }

        openApplication(applicationId, applicationIds, navigateToV3)
      }
      icon = faUserGraduate
      break

    case 'application_error':
      title = `${notification.metadata.details.offerName ?? 'Candidature spontanée'}`
      subtitle = `${notification.metadata.count} candidatures n'ont pas pu être traitées.`
      if (notification.metadata.count === 1) {
        subtitle = `La candidature de ${notification.metadata.stack[0]?.applicantName} n'a pas pu être traitée.`
      }
      if (notification.metadata.details.reason) {
        subtitle += `\n\n${notification.metadata.details.reason}`
      }
      onClick = () => {
        // The whole stack is passed so the modal can browse every application the notification groups
        const failedApplicationIds = applicationIdsFromStack(
          notification.metadata.stack
        )

        const [firstApplicationId] = failedApplicationIds
        if (firstApplicationId === undefined) {
          navigateToV3('/applications')
          return
        }

        openApplication(firstApplicationId, failedApplicationIds, navigateToV3)
      }
      icon = faUserGraduate
      break

    case 'sms_reply':
      title = `SMS de ${notification.metadata.stack[0]?.senderName}`
      subtitle = `${notification.metadata.stack[0]?.content}`
      if (notification.metadata.count && notification.metadata.count > 1) {
        title = `${notification.metadata.count} SMS de ${notification.metadata.stack[0]?.senderName}`
      }
      if (!Object.hasOwn(notification, 'isRead')) {
        title = `SMS de ${notification.metadata.stack[notification.metadata.stack.length - 1]?.senderName}`
        subtitle = `${notification.metadata.stack[notification.metadata.stack.length - 1]?.content}`
      }
      onClick = () => {
        const { applicantId, contactId, threadId } =
          notification.metadata.details
        if (applicantId) {
          navigateToV3(`/messaging?type=applicant&id=${applicantId}`)
        } else if (contactId) {
          navigateToV3(`/messaging?type=contact&id=${contactId}`)
        } else {
          window.location.href = `${legacyUrl}/admin/sms/thread/${threadId}/`
        }
      }
      icon = faMessage
      break

    case 'stats_file':
      title = `Rapport ${notification.metadata.stack[0]?.name?.toLowerCase() ?? ''} disponible`
      subtitle = `Statistiques ${notification.metadata.stack[0]?.period ?? ''}`
      onClick = () => {
        window.location.href = `${legacyUrl}/admin/statistiques/config`
      }
      icon = faChartPie
      break

    case 'sequence_added_recipients':
      title = `Séquence ${notification.metadata.details.sequenceName ?? ''}`
      subtitle = `${notification.metadata.details.count} nouveaux destinataires`
      icon = faEnvelopes
      break

    case 'test_completed': {
      title = `Test de ${notification.metadata.details.applicantName ?? ''} terminé`
      subtitle =
        notification.metadata.details.testName === undefined
          ? 'Les résultats sont disponibles sur sa fiche'
          : `${notification.metadata.details.testName} — résultats disponibles sur sa fiche`
      onClick = () => {
        window.location.href = `${legacyUrl}/admin/candidats/edit/${notification.metadata.details.applicantId}`
      }
      icon = faClipboardCheck
      break
    }

    case 'meeting_reported': {
      const {
        applicantId,
        contactId,
        date,
        participantName,
        title: meetingTitle
      } = notification.metadata.details
      title = 'Compte rendu ajouté au rendez-vous'
      subtitle = [meetingTitle, participantName]
        .filter((part) => part !== '')
        .join(' — ')
      onClick = () => {
        const search = new URLSearchParams({ view: 'done', type: 'meeting' })
        if (applicantId) {
          search.set('applicants', String(applicantId))
        } else if (contactId) {
          search.set('contacts', String(contactId))
        }
        if (date) {
          search.set('dueAfter', date)
          search.set('dueBefore', date)
        }
        navigateToV3(`/tasks?${search.toString()}`)
      }
      icon = faFileLines
      break
    }

    case 'shortlist_submitted': {
      const { orderId, contactName, applicantCount } =
        notification.metadata.details
      title = 'Retour sur une présentation client'
      subtitle = `${contactName || 'Le contact'} a classé ${applicantCount} profil${applicantCount > 1 ? 's' : ''}`
      onClick = () => {
        navigateToV3(`/orders/${orderId}`)
      }
      icon = faRankingStar
      break
    }

    case 'gdpr_anonymized_applicants': {
      const anonymizedCount = Number.parseInt(
        notification.metadata.details.count ?? '0',
        10
      )
      title = 'RGPD - anonymisation en cours'
      subtitle = `L'anonymisation de ${anonymizedCount} candidat${anonymizedCount > 1 ? 's' : ''} est en cours, cela peut prendre quelques minutes.`
      icon = faUserSecret
      break
    }

    case 'soon_event': {
      const date = new Date(notification.metadata.details.date ?? '')
      title = `${notification.metadata.details.readableType} prévu à ${String(date.getHours()).padStart(2, '0')}h${String(date.getMinutes()).padStart(2, '0')}`
      subtitle = notification.metadata.details.recipients.join(', ')
      icon = faCalendarDays
      onClick = () => {
        if (handlers.openTask) {
          handlers.openTask(notification.id)
          return
        }
        const { meetingLink, link } = notification.metadata.details
        window.location.href = meetingLink ?? `${legacyUrl}${link}`
      }
      break
    }

    case 'factor_answer': {
      title = `Réponse factor pour ${notification.metadata.details.company}`
      subtitle = notification.metadata.stack[0]?.answer || ''
      if (notification.metadata.count && notification.metadata.count > 1) {
        title = `${notification.metadata.count} réponses factor pour ${notification.metadata.details.company}`
      }
      subtitle =
        notification.metadata.stack[notification.metadata.stack.length - 1]
          ?.answer || ''
      onClick = () => {
        window.location.href = `${legacyUrl}/admin/factor/voir/${notification.metadata.stack[0]?.factor_request_id}`
      }
      icon = faMagnifyingGlassDollar
      break
    }

    case 'resume_inbound': {
      title = 'Nouveau CV reçu par email'
      const cvSource = notification.metadata.stack[0]?.cvSource?.name ?? ''
      subtitle = `${notification.metadata.details.applicant} a été créé`
      if (cvSource) {
        subtitle += ` via ${cvSource}`
      }
      onClick = () => {
        window.location.href = `${legacyUrl}/admin/candidats/edit/${notification.metadata.details.applicantId}`
      }
      icon = faEnvelopes
      break
    }

    case 'availability_response': {
      const lastIndex = notification.metadata.stack.length - 1
      title = 'Disponibilité mise à jour'

      subtitle = `${
        notification.metadata.stack[lastIndex]?.senderName
          ? `${notification.metadata.stack[lastIndex]?.senderName}`
          : `Le candidat #${notification.metadata.details.applicantId}`
      } a indiqué ${notification.metadata.stack[lastIndex]?.available === false ? 'son indisponibilité' : 'sa disponibilité'} : ${
        notification.metadata.stack[lastIndex]?.content
          ? `"${notification.metadata.stack[lastIndex]?.content}"`
          : 'aucune réponse'
      }`

      onClick = () => {
        window.location.href = `${legacyUrl}/admin/candidats/edit/${notification.metadata.details.applicantId}`
      }
      icon = faMessage
      break
    }

    case 'email_received': {
      title = `Email de ${notification.metadata.stack[0]?.sender}`
      subtitle = `${notification.metadata.stack[0]?.subject} - ${notification.metadata.stack[0]?.content}`
      if (!Object.hasOwn(notification, 'isRead')) {
        title = `Email de ${notification.metadata.stack[notification.metadata.stack.length - 1]?.sender}`
        subtitle = `${notification.metadata.stack[notification.metadata.stack.length - 1]?.content}`
      }
      onClick = () => {
        const { applicantId, contactId } = notification.metadata.details
        if (applicantId) {
          navigateToV3(`/messaging?type=applicant&id=${applicantId}`)
        } else if (contactId) {
          navigateToV3(`/messaging?type=contact&id=${contactId}`)
        }
      }
      icon = faInboxIn
      break
    }
  }
  return { title, subtitle, icon, onClick }
}

export type NotificationsViewerProps = PropsWithChildren<{
  classname?: string
  currentUserId?: number
  defaultCount?: number
}>
